/**
 * Partner Dashboard Page
 * Refactored into modular components for maintainability
 */
import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  getPartnerProfile, getPartnerAnalytics, getPartnerWidgets,
  getPartnerPayouts, getSDKDocumentation
} from '../lib/api';
import { motion } from 'framer-motion';
import { Layers, Key, Code, Palette, Wallet, CheckCircle, Loader2 } from 'lucide-react';

// Modular components
import {
  PartnerApplicationCard,
  PartnerStatsGrid,
  APICredentialsTab,
  WidgetsTab,
  BrandingTab,
  PayoutsTab
} from '../components/partner';

const PartnerDashboard = () => {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [sdkDocs, setSDKDocs] = useState(null);

  useEffect(() => {
    loadPartnerData();
  }, []);

  const loadPartnerData = async () => {
    try {
      const res = await getPartnerProfile();
      setPartner(res.data);
      
      // Load additional data
      const [analyticsRes, widgetsRes, payoutsRes, docsRes] = await Promise.all([
        getPartnerAnalytics(30),
        getPartnerWidgets(),
        getPartnerPayouts(),
        getSDKDocumentation()
      ]);
      
      setAnalytics(analyticsRes.data);
      setWidgets(widgetsRes.data);
      setPayouts(payoutsRes.data);
      setSDKDocs(docsRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setPartner(null);
      } else {
        logger.error('Failed to load partner data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationSuccess = (newPartner) => {
    setPartner(newPartner);
    loadPartnerData();
  };

  const handleKeysRegenerated = (apiKey, secretKey) => {
    setPartner(prev => ({ ...prev, api_key: apiKey, secret_key: secretKey }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  // Not a partner - show application
  if (!partner) {
    return (
      <Layout>
        <PartnerApplicationCard onSuccess={handleApplicationSuccess} />
      </Layout>
    );
  }

  // Partner dashboard
  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="partner-dashboard"
      >
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Layers className="w-8 h-8 text-emerald-600" />
              Partner Dashboard
            </h1>
            <p className="text-muted-foreground">{partner.company_name}</p>
          </div>
          <Badge className={partner.status === 'approved' ? 'bg-emerald-500/20 text-emerald-600' : 'bg-amber-500/20 text-amber-600'}>
            {partner.status === 'approved' ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
            {partner.status}
          </Badge>
        </div>

        {/* Stats Grid */}
        <PartnerStatsGrid analytics={analytics} widgetsCount={widgets.length} />

        {/* Tabs */}
        <Tabs defaultValue="credentials" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-12 rounded-xl">
            <TabsTrigger value="credentials" className="rounded-lg">
              <Key className="w-4 h-4 mr-2" />
              API Keys
            </TabsTrigger>
            <TabsTrigger value="widgets" className="rounded-lg">
              <Code className="w-4 h-4 mr-2" />
              Widgets
            </TabsTrigger>
            <TabsTrigger value="branding" className="rounded-lg">
              <Palette className="w-4 h-4 mr-2" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="payouts" className="rounded-lg">
              <Wallet className="w-4 h-4 mr-2" />
              Payouts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="credentials">
            <APICredentialsTab 
              partner={partner} 
              onKeysRegenerated={handleKeysRegenerated}
              sdkDocs={sdkDocs}
            />
          </TabsContent>

          <TabsContent value="widgets">
            <WidgetsTab 
              widgets={widgets} 
              onWidgetCreated={loadPartnerData}
            />
          </TabsContent>

          <TabsContent value="branding">
            <BrandingTab partner={partner} />
          </TabsContent>

          <TabsContent value="payouts">
            <PayoutsTab 
              analytics={analytics}
              payouts={payouts}
              onPayoutRequested={loadPartnerData}
            />
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default PartnerDashboard;
